import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// 照合APIは管理者認証必須
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function KYCVerification() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [requestList, setRequestList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    fetchVerificationData();
    fetchRequestList();
  }, [userId]);

  const fetchRequestList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/kyc/verification-requests`,
        authConfig()
      );
      if (response.data.success) {
        setRequestList(response.data.requests);
        const idx = response.data.requests.findIndex(r => String(r.user_id) === String(userId));
        setCurrentIndex(idx);
      }
    } catch (err) {
      console.warn('照合依頼一覧の取得に失敗:', err);
    }
  };

  const fetchVerificationData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(
        `${API_URL}/api/kyc/verification/${userId}`,
        authConfig()
      );
      setVerificationData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || '照合データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!confirm('この申請を承認してもよろしいですか？')) return;

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/kyc/verification/${userId}/approve`,
        {},
        authConfig()
      );
      alert(response.data.message);
      // 次の依頼があれば遷移、なければ再取得
      const nextRequest = requestList.find((r, i) => i > currentIndex && String(r.user_id) !== String(userId));
      if (nextRequest) {
        navigate(`/admin/kyc-verification/${nextRequest.user_id}`);
      } else {
        fetchVerificationData();
        fetchRequestList();
      }
    } catch (err) {
      alert(err.response?.data?.message || '承認処理に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('却下理由を入力してください');
      return;
    }
    if (!confirm('この申請を却下してもよろしいですか？')) return;

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/kyc/verification/${userId}/reject`,
        { rejection_reason: rejectionReason.trim() },
        authConfig()
      );
      alert(response.data.message);
      const nextRequest = requestList.find((r, i) => i > currentIndex && String(r.user_id) !== String(userId));
      if (nextRequest) {
        navigate(`/admin/kyc-verification/${nextRequest.user_id}`);
      } else {
        fetchVerificationData();
        fetchRequestList();
      }
    } catch (err) {
      alert(err.response?.data?.message || '却下処理に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      navigate(`/admin/kyc-verification/${requestList[currentIndex - 1].user_id}`);
    }
  };

  const goToNext = () => {
    if (currentIndex < requestList.length - 1) {
      navigate(`/admin/kyc-verification/${requestList[currentIndex + 1].user_id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">エラー</h2>
            <p className="text-gray-600 mb-6">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!verificationData) return null;

  const { submission, user, step1Data } = verificationData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* ヘッダー */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">📋 身分証明書の照合</h1>
            {/* 件数ナビゲーション */}
            {requestList.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={goToPrev}
                  disabled={currentIndex <= 0}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  ←
                </button>
                <span className="text-sm font-semibold text-gray-700">
                  {currentIndex >= 0 ? currentIndex + 1 : '?'} / {requestList.length} 件
                </span>
                <button
                  onClick={goToNext}
                  disabled={currentIndex >= requestList.length - 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  →
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">依頼時間:</span>
              <span className="ml-2 font-semibold">
                {new Date(submission.manual_verification_requested_at).toLocaleString('ja-JP')}
              </span>
            </div>
            <div>
              <span className="text-gray-500">依頼者名:</span>
              <span className="ml-2 font-semibold">{user.username || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">ユーザーID:</span>
              <span className="ml-2 font-semibold">{userId}</span>
            </div>
            <div>
              <span className="text-gray-500">メール:</span>
              <span className="ml-2 font-semibold">{user.email || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* 身分証画像（左）+ Step1入力データ（右） */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* 左: アップロード画像 */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">📄 身分証明書</h2>
            <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
              {submission.document_file_path ? (
                <img
                  src={submission.document_file_path.startsWith('http') ? submission.document_file_path : `${API_URL}${submission.document_file_path}`}
                  alt="身分証明書"
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                />
              ) : (
                <p className="text-gray-500 text-center py-8">画像がアップロードされていません</p>
              )}
            </div>
          </div>

          {/* 右: Step1 入力データ */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">📝 Step1 入力データ</h2>
            {step1Data ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-500 mb-1">氏名</div>
                  <div className="font-semibold text-gray-800">{step1Data.full_name || 'N/A'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-500 mb-1">生年月日</div>
                  <div className="font-semibold text-gray-800">
                    {step1Data.birth_date ? new Date(step1Data.birth_date).toLocaleDateString('ja-JP') : 'N/A'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-500 mb-1">住所</div>
                  <div className="font-semibold text-gray-800">
                    {step1Data.postal_code && <span>〒{step1Data.postal_code} </span>}
                    {step1Data.address || 'N/A'}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Step1データがありません</p>
            )}
          </div>
        </div>

        {/* OCR結果（参考情報） */}
        {submission.ocr_result && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">🤖 OCR結果（参考）</h2>
            <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(submission.ocr_result, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* 承認/却下ボタン */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">✅ 照合結果</h2>

          {/* 却下理由入力 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              却下理由（却下する場合は必須）
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="例：画像が不鮮明で氏名が確認できません。別の画像で再度アップロードしてください。"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all resize-none"
              rows="4"
            />
          </div>

          {/* ボタン */}
          <div className="flex gap-4">
            <button
              onClick={handleApprove}
              disabled={processing}
              className="flex-1 px-6 py-4 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-bold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? '処理中...' : '✅ 承認する'}
            </button>
            <button
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
              className="flex-1 px-6 py-4 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-bold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? '処理中...' : '❌ 却下する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
